import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, TrendingUp, Eye, ArrowRight, BarChart3, Plus } from 'lucide-react';
import { adminGetDashboard } from '../../services/api';
import { formatPrice } from '../../utils/constants';
import AdminLayout from './AdminLayout';
import './Admin.css';

const STATUS_COLOR = { NEW:'#3b82f6', CONTACTED:'#f59e0b', QUALIFIED:'#10b981', CLOSED:'#6b7280', LOST:'#ef4444' };

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminGetDashboard()
      .then(res => setData(res.data.data))
      .catch(() => setError('Could not load dashboard. Check server connection.'))
      .finally(() => setLoading(false));
  }, []);

  const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-page__header">
          <div>
            <h1>Dashboard</h1>
            <p>Overview of your platform activity</p>
          </div>
          <Link to="/admin/properties/new" className="btn-primary"><Plus size={15}/> Add Property</Link>
        </div>

        {loading && <div className="admin-loading"><div className="admin-spinner"/><p>Loading dashboard...</p></div>}
        {error && <div style={{background:'#fff0f0',border:'1px solid rgba(234,46,43,0.2)',padding:'14px 18px',borderRadius:'var(--radius)',color:'var(--primary)',fontSize:'14px',marginBottom:'20px'}}>{error}</div>}

        {data && (
          <>
            <div className="stat-cards">
              <div className="stat-card">
                <div className="stat-card__icon" style={{background:'rgba(234,46,43,0.08)',color:'var(--primary)'}}><Building2 size={22}/></div>
                <div>
                  <p className="stat-card__label">Total Properties</p>
                  <p className="stat-card__value">{data.totalProperties ?? 0}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card__icon" style={{background:'rgba(16,185,129,0.1)',color:'var(--success)'}}><Users size={22}/></div>
                <div>
                  <p className="stat-card__label">Total Leads</p>
                  <p className="stat-card__value">{data.totalLeads ?? 0}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card__icon" style={{background:'rgba(245,158,11,0.1)',color:'var(--warning)'}}><TrendingUp size={22}/></div>
                <div>
                  <p className="stat-card__label">This Month</p>
                  <p className="stat-card__value">{data.leadsThisMonth ?? 0}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card__icon" style={{background:'rgba(59,130,246,0.1)',color:'var(--info)'}}><Eye size={22}/></div>
                <div>
                  <p className="stat-card__label">Top Property</p>
                  <p className="stat-card__value" style={{fontSize:'13px',lineHeight:1.3,fontFamily:'var(--font-body)',fontWeight:600}}>{data.mostViewedProperty?.title || '—'}</p>
                </div>
              </div>
            </div>

            <div className="admin-grid-2">
              <div className="admin-card">
                <div className="admin-card__header">
                  <h3>Recent Leads</h3>
                  <Link to="/admin/leads">View All <ArrowRight size={13}/></Link>
                </div>
                {data.recentLeads?.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr><th>Name</th><th>Phone</th><th>Property</th><th>Status</th><th>Date</th></tr>
                    </thead>
                    <tbody>
                      {data.recentLeads.map(l => (
                        <tr key={l.id}>
                          <td><strong>{l.name}</strong></td>
                          <td style={{fontSize:'12px'}}>{l.phone}</td>
                          <td className="td-truncate" style={{fontSize:'12px',color:'var(--text-2)'}}>{l.propertyTitle || 'General'}</td>
                          <td>
                            <span className="status-badge" style={{background:STATUS_COLOR[l.leadStatus]+'18',color:STATUS_COLOR[l.leadStatus]}}>
                              {l.leadStatus}
                            </span>
                          </td>
                          <td style={{fontSize:'12px',color:'var(--text-3)',whiteSpace:'nowrap'}}>{fmt(l.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{padding:'32px',textAlign:'center',color:'var(--text-3)',fontSize:'14px'}}>No leads yet. Leads from your website forms will appear here.</p>
                )}
              </div>

              <div className="admin-card">
                <div className="admin-card__header"><h3>Quick Actions</h3></div>
                <div className="quick-actions">
                  <Link to="/admin/properties/new" className="quick-action"><Building2 size={17}/> Add New Property</Link>
                  <Link to="/admin/properties" className="quick-action"><BarChart3 size={17}/> Manage Properties</Link>
                  <Link to="/admin/leads" className="quick-action"><Users size={17}/> View All Leads</Link>
                </div>
                {data.mostViewedProperty && (
                  <div className="admin-highlight" style={{marginTop:'18px'}}>
                    <p className="admin-highlight__label">🏆 Top Viewed Property</p>
                    <p className="admin-highlight__title">{data.mostViewedProperty.title}</p>
                    <p className="admin-highlight__meta">{data.mostViewedProperty.city} • {formatPrice(data.mostViewedProperty.price)}</p>
                    <p className="admin-highlight__views"><Eye size={11}/> {data.mostViewedProperty.viewCount} views</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
